import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { Toast, ToastsContainer } from "../components/ui/Toast";
import { uuid } from "../utils";

export type ToastDetails = {
  id: string;
  title?: string;
  message: string;
  disappearing: boolean;
  type?: "error" | "success";
  disappearAutomatically?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
};

type ProviderProps = {
  children: JSX.Element;
};

const ToastContext = createContext((toast: Omit<ToastDetails, "id" | "disappearing">) => () => {});

export const TOAST_TTL = 8000;

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: ProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ToastDetails[]>([]);

  const removeToast = useCallback((id: string): void => {
    setToasts((toasts) => toasts.map((n) => (n.id === id ? { ...n, disappearing: true } : n)));
    setTimeout(() => {
      setToasts((toasts) => toasts.filter((n) => n.id !== id));
    }, 1000);
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastDetails, "id" | "disappearing">): (() => void) => {
      const id = uuid();
      const disappearAutomatically = toast.disappearAutomatically ?? true;
      setToasts((toasts) => {
        return [...toasts, { ...toast, id, disappearing: false }];
      });

      if (disappearAutomatically) {
        setTimeout(() => {
          removeToast(id);
        }, TOAST_TTL);
      }

      return () => {
        removeToast(id);
      };
    },
    [removeToast],
  );

  useEffect(() => {
    if (toasts.filter((t) => !t.disappearing).length > 2) removeToast(toasts.find((t) => !t.disappearing)!.id);
  }, [toasts, removeToast]);

  return (
    <ToastContext.Provider value={addToast}>
      <ToastsContainer>
        {toasts.map((n) => (
          <Toast key={n.id} {...n} onClose={() => removeToast(n.id)} />
        ))}
      </ToastsContainer>
      {children}
    </ToastContext.Provider>
  );
}
